'use client'
import { useEffect, useRef } from 'react'
import styled from 'styled-components'
import { useGlobalDefaultShoppingAddress } from '@/features/account/services/Addresses/hooks'
import { Box, Row } from '@shipt/design-system-layouts'
import { MobileAddressButton } from '@/features/shared/components/Headers/GlobalHeader/MobileAddressButton'
import { HamburgerMenuButton } from '@/features/shared/components/HamburgerMenu/HamburgerMenuButton'
import { HeaderBanners } from '@/features/shared/components/Headers/Header/HeaderBanners'
import { useScrollWithThreshold } from '@/features/shared/components/Headers/useScrollWithThreshold'
import { AddressButton } from '@/features/shared/components/Headers/GlobalHeader/AddressButton'
import { CartsButton } from '@/features/shared/components/Headers/GlobalHeader/CartsButton'
import { LiveOrderStatusBanner } from '@/features/shared/components/HeaderBanner/banners/LiveOrderStatusBanner'
import {
  STICKY_HEADER_ID,
  MOBILE_BG_POSITIONER_ID,
  MOBILE_STICKY_NAV_ID,
} from '@/features/shared/hooks/useHeaderRect'
import { screenSizes } from '@shipt/design-system-themes'
import { spacingPx } from '@/features/shared/theme/tokens'
import {
  GlobalHeaderLogoLink,
  GlobalHeaderContainer,
  GlobalHeaderElement,
} from '@/features/shared/components/Headers/GlobalHeader/GlobalHeaderComponents'
import { useRouteToSearchFromAutocomplete } from '@/features/shared/components/Autocomplete/useRouteToSearchFromAutocomplete'
import { ProductSearchAutocomplete } from '@/features/shared/components/Autocomplete/ProductSearchAutocomplete'
import {
  AutocompletePill,
  CategorySubNavContainer,
} from '@/features/shared/components/Headers/Header/styles'
import { OrderButton } from '@/features/shared/components/Headers/Header/RightMenuItems'
import dynamic from 'next/dynamic'
import { useQueryCMSGlobalHeaderCategories } from '@/features/cms/services/CMS/queries'
import { AccountCreationButtons } from '@/features/shared/components/GuestUser/AccountCreationButtons'
import {
  GlobalPriorityNavigation,
  GlobalMobileNavigation,
} from '@/features/shop/components/CategorySubNav/GlobalPriorityNavigation'
import { MobileAddressWrapper } from '@/features/shared/components/Headers/MobileAddressWrapper'
import { useSession } from '@/features/authentication/utils/authentication/hooks/useSession'
import { GlobalHeaderVisuallyHiddenLinksSEO } from '@/features/shared/components/Headers/GlobalHeader/GlobalHeaderVisuallyHiddenLinksSEO'
import { BusinessBadge } from '@/features/business/components/BusinessBadge'
import { usePersona } from '@/features/business/hooks/usePersona'
import { useGetImperativeIsDialogOpened } from '@/features/shared/state/Dialog/hooks'
import { useHandleDialog } from '@/features/shared/state/Dialog/useHandleDialog'
import { DynamicShoppingPersonaSelectorModal } from '@/features/shop/components/Modal/ShoppingPersonaSelectorModal/DynamicShoppingPersonaSelectorModal'
import {
  useQueryActiveOrders,
  useQueryIncompleteOrders,
} from '@/features/shop/services/Orders/queries'
import { useExperiment } from '@/features/shared/services/Experiments/hooks'
import { ExperimentType } from '@shared/types/experiments'

const UpcomingOrdersButton = dynamic(
  async () =>
    (await import('@/features/shared/components/Headers/Header/UpcomingOrdersButton'))
      .UpcomingOrdersButton,
  { loading: () => <OrderButton /> },
)

export const GlobalAnimatedHeader = ({
  excludeSubheader,
  isGlobalHomepage,
}: {
  excludeSubheader?: boolean
  isGlobalHomepage?: boolean
}) => {
  const isPastScrollThreshold = useScrollWithThreshold(90)
  const hasAddress = Boolean(useGlobalDefaultShoppingAddress().street1)
  const onProductSearch = useRouteToSearchFromAutocomplete({
    isMultiStoreSearch: true,
  })
  const { data: globalHeaderCategories } = useQueryCMSGlobalHeaderCategories(!excludeSubheader)
  const isLoggedIn = Boolean(useSession().userId)
  const { data: activeOrders } = useQueryActiveOrders()

  const showMobileNav = !isLoggedIn && !excludeSubheader

  const shouldShowAuthButtons = !isLoggedIn && !hasAddress
  const { isFeatureEnabled: isBusinessEnabled, groups, personaSelected } = usePersona()
  const hasGroups = !!groups?.length
  const hasFetchedIncompleteOrders = useQueryIncompleteOrders().fetchStatus === 'idle'
  const { active: isHolidayExperimentActive } = useExperiment(ExperimentType.HOLIDAY_GHP)
  const isHolidayGhpActive = Boolean(isGlobalHomepage) && isHolidayExperimentActive
  const getIsDialogOpened = useGetImperativeIsDialogOpened()
  const { openDialog } = useHandleDialog()
  const hasShownPersonaModalRef = useRef(false)

  useEffect(() => {
    if (hasShownPersonaModalRef.current) return
    if (getIsDialogOpened()) return
    if (!isBusinessEnabled) return
    if (!hasGroups) return
    if (personaSelected) return
    if (!hasFetchedIncompleteOrders) return

    hasShownPersonaModalRef.current = true
    openDialog(DynamicShoppingPersonaSelectorModal)
  }, [
    isBusinessEnabled,
    hasGroups,
    personaSelected,
    hasFetchedIncompleteOrders,
    getIsDialogOpened,
    openDialog,
  ])

  return (
    <>
      <GlobalAnimatedHeaderElement id={STICKY_HEADER_ID}>
        <LiveOrderStatusBanner />
        {!!isLoggedIn && <HeaderBanners />}
        <GlobalHeaderContainer contentMaxWidth as="nav" $holidayGHP={isHolidayGhpActive}>
          <HeaderMainRow spacing={{ base: 'sm', sm: 'lg', lg: 'xl' }}>
            <HeaderLeftControls spacing="sm">
              <HamburgerMenuButton isGlobalMenu holidayGHP={isHolidayGhpActive} />
              <Row
                align="center"
                spacing="sm"
                display={{
                  base: shouldShowAuthButtons ? 'flex' : 'none',
                  md: 'flex',
                }}
              >
                <HeaderLogoLink holidayGHP={isHolidayGhpActive} />
                <BusinessBadge />
              </Row>
            </HeaderLeftControls>
            <CenteredLogoRow
              display={{
                base: shouldShowAuthButtons ? 'none' : 'flex',
                md: 'none',
              }}
            >
              <HeaderLogoLink
                isPastScrollThreshold={isPastScrollThreshold}
                holidayGHP={isHolidayGhpActive}
              />
              <BusinessBadge />
            </CenteredLogoRow>
            <MobileAddressWrapper
              className="hideAboveTablet"
              justify="center"
              isPastScrollThreshold={isPastScrollThreshold}
            >
              <MobileAddressButton $holidayGHP={isHolidayGhpActive} />
            </MobileAddressWrapper>
            <GlobalAutocomplete data-is-past-scroll-threshold={isPastScrollThreshold}>
              <GlobalSearchAutocomplete
                onProductSearch={onProductSearch}
                pinToTop
                placeholder="What are you looking for?"
                showDimEffect
                isMultiStoreSearch
                showHolidaySpiderweb={isHolidayGhpActive}
              />
            </GlobalAutocomplete>
            <HeaderRightControls
              spacing={{
                base: 'sm',
                lg: 'lg',
              }}
              flexShrink={0}
              align="center"
            >
              <Box display={shouldShowAuthButtons ? 'block' : { base: 'none', md: 'block' }}>
                <AddressButton holidayGHP={isHolidayGhpActive} />
              </Box>
              {!!isLoggedIn && (
                <Box display={{ base: 'none', md: 'block' }}>
                  {activeOrders && activeOrders.length > 0 ? (
                    <UpcomingOrdersButton
                      activeOrders={activeOrders}
                      holidayGHP={isHolidayGhpActive}
                    />
                  ) : (
                    <OrderButton holidayGHP={isHolidayGhpActive} />
                  )}
                </Box>
              )}
              {!!hasAddress && <CartsButton holidayGHP={isHolidayGhpActive} />}
              {!!shouldShowAuthButtons && (
                <FadeOnScrollBox isPastScrollThreshold={isPastScrollThreshold}>
                  <AccountCreationButtons isHeader holidayGHP={isHolidayGhpActive} />
                </FadeOnScrollBox>
              )}
            </HeaderRightControls>
          </HeaderMainRow>
        </GlobalHeaderContainer>
        {!excludeSubheader && (
          <CategorySubNavWrapper>
            <CategorySubNavContainer contentMaxWidth $holidayGHP={isHolidayGhpActive}>
              {!!globalHeaderCategories?.data && (
                <GlobalPriorityNavigation
                  globalHeaderCategories={globalHeaderCategories.data}
                  holidayGHP={isHolidayGhpActive}
                />
              )}
            </CategorySubNavContainer>
          </CategorySubNavWrapper>
        )}
        <MobileContentBackgroundPositioner
          showMobileNav={showMobileNav}
          className="hideAboveTablet"
          id={MOBILE_BG_POSITIONER_ID}
        >
          <MobileContentBackground
            showMobileNav={showMobileNav}
            isPastScrollThreshold={isPastScrollThreshold}
            $holidayGHP={isHolidayGhpActive}
          />
          {!!showMobileNav && (
            <CategorySubNavContainer
              contentMaxWidth
              id={MOBILE_STICKY_NAV_ID}
              $holidayGHP={isHolidayGhpActive}
            >
              {!!globalHeaderCategories?.data && (
                <GlobalMobileNavigation
                  globalHeaderCategories={globalHeaderCategories.data}
                  holidayGHP={isHolidayGhpActive}
                />
              )}
            </CategorySubNavContainer>
          )}
        </MobileContentBackgroundPositioner>
      </GlobalAnimatedHeaderElement>

      {!isLoggedIn && <GlobalHeaderVisuallyHiddenLinksSEO />}
    </>
  )
}

type IsPastScrollThreshold = { isPastScrollThreshold: boolean }
type PinToTop = { pinToTop: boolean }

const GlobalAnimatedHeaderElement = styled(GlobalHeaderElement)`
  @media ${screenSizes.tablet} {
    border-bottom: 0.5px solid
      ${({ theme }) => theme.color.border.base.neutral.subtle};
  }
`

const HeaderLogoLink = styled(GlobalHeaderLogoLink)<Partial<IsPastScrollThreshold>>`
  display: flex;
  align-items: center;
  transition: opacity 0.3s ease;
  opacity: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? 0 : 1)};

  & svg {
    height: 32px;
  }

  @media ${screenSizes.tablet} {
    opacity: 1;

    & svg {
      height: unset;
    }
  }

  @media ${screenSizes.max_mobile} {
    justify-self: center;
  }
`

const CenteredLogoRow = styled(Row)`
  @media ${screenSizes.max_mobile} {
    grid-column: 2;
    justify-self: center;
  }
`

const HeaderMainRow = styled(Row)`
  justify-content: space-between;
  align-items: center;

  @media ${screenSizes.max_mobile} {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    align-items: center;
  }
`

const HeaderLeftControls = styled(Row)`
  align-items: center;

  @media ${screenSizes.max_mobile} {
    grid-column: 1;
    justify-self: start;
  }
`

const HeaderRightControls = styled(Row)`
  @media ${screenSizes.max_mobile} {
    grid-column: 3;
    justify-self: end;
    position: relative;
    z-index: 2;
    text-wrap: nowrap;
  }
`

const FadeOnScrollBox = styled(Box)<IsPastScrollThreshold>`
  @media ${screenSizes.max_mobile} {
    transition: opacity 0.3s ease;
    opacity: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? 0 : 1)};
  }
`

const MobileContentBackgroundPositioner = styled.div<{
  showMobileNav: boolean
}>`
  /* do not remove, a fixed height is needed here currently to prevent a browser bug where
    the header bounces due to a conflict between position: sticky & our scroll threshold logic */
  height: ${({ showMobileNav }) => (showMobileNav ? '7.625rem' : '4.625rem')};

  /* do not remove, this is to allow the elements covered by this element to be clickable */
  pointer-events: none;

  ${CategorySubNavContainer} {
    pointer-events: all;
  }
`

const MobileContentBackground = styled.div<
  IsPastScrollThreshold & { showMobileNav: boolean; $holidayGHP?: boolean }
>`
  transition: height 0.6s ease;
  height: ${({ isPastScrollThreshold }) => (isPastScrollThreshold ? 0 : 4.5)}rem;
  background-color: ${({ theme, $holidayGHP }) =>
    $holidayGHP
      ? theme.color.background.base.brand.primary
      : theme.color.background.base.neutral.subtle_00};
  border-bottom: ${({ theme, showMobileNav }) =>
    showMobileNav ? 'none' : `0.5px solid ${theme.color.border.base.neutral.subtle}`};
`

const GlobalAutocomplete = styled(AutocompletePill)<{
  'data-is-past-scroll-threshold': boolean
}>`
  @media ${screenSizes.max_mobile} {
    position: absolute;
    z-index: 1;
    transition: all 0.6s ease;
    top: calc(100% + 1.25rem);
    translate: 0 0;
    left: 1rem;
    right: 1rem;
    padding: ${spacingPx('xs')};
    margin-top: -0.25rem;
    height: 2.75rem;
    width: auto;

    &[data-is-past-scroll-threshold='true'] {
      top: 50%;
      translate: 0 -50%;
      left: 3.4rem;
      right: 4.3rem;
      margin-top: 0;
    }
  }
`

const GlobalSearchAutocomplete = styled(ProductSearchAutocomplete)<PinToTop>`
  position: relative;
  color: ${({ pinToTop, theme }) => (pinToTop ? theme.gray900 : theme.gray600)};

  & img[role='presentation'] {
    top: -4px;
    right: -2px;
    height: calc(100% + 8px);
  }

  &:focus-within img[role='presentation'] {
    top: 0;
    right: 0;
    height: 100%;
  }

  @media ${screenSizes.max_mobile} {
    & img[role='presentation'] {
      top: -2px;
      height: calc(100% + 4px);
    }
  }

  & button {
    min-width: ${({ pinToTop }) => (pinToTop ? '2.5rem' : '3.688rem')};
    height: ${({ pinToTop }) => (pinToTop ? '2.5rem' : '3.688rem')};
  }
`

const CategorySubNavWrapper = styled.div`
  position: relative;
  z-index: -1;

  @media ${screenSizes.max_mobile} {
    display: none;
  }

  & > div {
    border-bottom: 0.5px solid
      ${({ theme }) => theme.color.border.base.neutral.subtle};
  }
`
